import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "ApiClient";
import { ApiURL, getEnabled } from "utils/Actions";
import { showErrorModal } from "utils/hooks/handleNotification";
import { common } from "utils/Wording";
import {
  applySort,
  createPaginatedTableData,
  generateTableData
} from "utils/hooks/studentTableActions";
import { ApiUgOsQueryParams, SRSApplicantsState, SRSApplicantType } from "utils/Types";
import { SCHOOL_RATED, TABLE_FILTER, WHITELABEL } from "utils/Constants";
import { defaultSorting } from "utils/AssetsData";
import { RootState } from "redux/store";
import { buildSourceQueryParams } from "utils/Actions";

const initialState: SRSApplicantsState = {
  isLoading: false,
  isError: false,
  origin: [],
  tableData: [],
  paginatedTableData: [],
  currentPage: 1,
  sorts: {},
  filters: [
    {
      name: TABLE_FILTER.SCHOOL_RATED,
      rated: false,
      unrated: false,
      partial: false
    }
  ],
  search: "",
  searchOrigin: "",
  // Dashboard metrics
  statistics: {
    totalApplicants: 0,
    ratedApplicants: 0,
    unratedApplicants: 0,
    partiallyRatedApplicants: 0
  }
};

// optional source param = 'ug' | 'os'
// https://uac-cloud.atlassian.net/browse/ABKL-82
export const fetchSRSApplicants = createAsyncThunk(
  "srsApplicants/fetch",
  async ({ source = null }: ApiUgOsQueryParams, thunkAPI) => {
    const queryParams = buildSourceQueryParams(thunkAPI.getState() as RootState, source);
    try {
      const res = await api.get(ApiURL.srsApplicants, queryParams);
      if (res?.data?.status === "ERROR") {
        throw new Error(common.loadingError);
      }
      const transformedData = res?.data?.data
        ? res.data.data.map((applicant: SRSApplicantType) => ({
            ...applicant,
            // Adjust for data discrepancy from the backend API
            givenName1: applicant.givenName1.toUpperCase(),
            givenName2: applicant?.givenName2?.toUpperCase(),
            familyName: applicant.familyName.toUpperCase(),
            schoolRated: SCHOOL_RATED[applicant.schoolRated as keyof typeof SCHOOL_RATED],
            fullName: `${applicant.givenName1.toUpperCase()} ${applicant.familyName.toUpperCase()}`
          }))
        : [];
      return transformedData;
    } catch (error: any) {
      console.error(error);
      showErrorModal({
        title: common.failAsync,
        errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
      });
      return [];
    }
  },
  {
    condition: ({ source = null }, { getState }) => {
      const whiteLabel = (getState() as RootState).properties.whiteLabel as WHITELABEL | null;
      return getEnabled(source, whiteLabel);
    }
  }
);

const SRSApplicantsSlice = createSlice({
  name: "srsApplicants",
  initialState,
  reducers: {
    updateCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.paginatedTableData = createPaginatedTableData(action.payload, state.tableData);
    },
    sortTable: (state, action: PayloadAction<any>) => {
      state.sorts = action.payload.sortObj;
      state.tableData = generateTableData(
        state.tableData,
        state.search,
        state.filters,
        state.sorts
      );
      state.paginatedTableData = createPaginatedTableData(state.currentPage, state.tableData);
    },
    filterTable: (state, action: PayloadAction<any>) => {
      state.filters = action.payload.newFilters;
      state.currentPage = 1;
      state.tableData = generateTableData(state.origin, state.search, state.filters, state.sorts);
      state.paginatedTableData = createPaginatedTableData(state.currentPage, state.tableData);
    },
    searchTable: (state, action: PayloadAction<{ target: string; targetOrigin: string }>) => {
      state.search = action.payload.target;
      state.searchOrigin = action.payload.targetOrigin;
      state.currentPage = 1;
      state.tableData = generateTableData(state.origin, state.search, state.filters, state.sorts);
      state.paginatedTableData = createPaginatedTableData(state.currentPage, state.tableData);
    },
    applyCriteria: (state, action) => {
      const { currentPage, filters, sorts, search } = action.payload;

      const newTableData = generateTableData(state.origin, search, filters, sorts);
      const newPaginatedData = createPaginatedTableData(currentPage, newTableData);

      state.currentPage = currentPage;
      state.sorts = sorts;
      state.filters = filters;
      state.search = search;
      state.tableData = newTableData;
      state.paginatedTableData = newPaginatedData;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchSRSApplicants.pending, () => {
      return {
        ...initialState,
        isLoading: true
      };
    });
    builder.addCase(
      fetchSRSApplicants.fulfilled,
      (state, action: PayloadAction<SRSApplicantType[]>) => {
        state.origin = action.payload;
        state.tableData = applySort(defaultSorting, state.origin);
        state.paginatedTableData = createPaginatedTableData(1, state.tableData);
        state.isLoading = false;

        // Calculate dashboard metrics
        const { totalApplicants, ratedApplicants, unratedApplicants, partiallyRatedApplicants } =
          state.origin.reduce(
            (acc, applicant) => {
              if (applicant.schoolRated === SCHOOL_RATED.RATED) {
                acc.ratedApplicants++;
              } else if (applicant.schoolRated === SCHOOL_RATED.NOT_RATED) {
                acc.unratedApplicants++;
              } else if (applicant.schoolRated === SCHOOL_RATED.PARTIAL) {
                acc.partiallyRatedApplicants++;
              }
              acc.totalApplicants++;
              return acc;
            },
            {
              totalApplicants: 0,
              ratedApplicants: 0,
              unratedApplicants: 0,
              partiallyRatedApplicants: 0
            }
          );

        state.statistics = {
          ...state.statistics,
          totalApplicants,
          ratedApplicants,
          unratedApplicants,
          partiallyRatedApplicants
        };
      }
    );
    builder.addCase(fetchSRSApplicants.rejected, () => {
      return {
        ...initialState,
        isError: true
      };
    });
  }
});

export const { updateCurrentPage, sortTable, filterTable, searchTable, applyCriteria } =
  SRSApplicantsSlice.actions;

export default SRSApplicantsSlice.reducer;
